import { getMetricScoreValue, metricBarScoreColor } from '@/lib/stock-score-fields';
import {
  RATING_TIER_GREEN,
  RATING_TIER_RED,
  RATING_TIER_YELLOW,
  ratingTierId,
  scoreTierLabel,
} from '@/lib/rating-tiers';

export const FALLBACK_PERCENTAGE_FIELDS = new Set([
  'EbitMargin', 'NetIncomeMargin', 'FCFMargin', 'ROE', 'ROA', 'ROCE',
  'RevenueGrowthFwd1Y', 'Exp3YRevenueGrowthCAGR', 'RevenueFwd5YCAGR',
  'RevGrowthhistCAGR3y', 'EPSGrowthFY1', 'EPSGrowthFY2', 'EPSGrowthFY3',
  'FCFYield', 'DivdendYield',
  'FCFMarginForecastFY1', 'FCFMarginForecastFY2', 'FCFMarginForecastFY3',
  '1weekreturn', '1monthreturn', '3monthreturn', 'YTDreturn',
  '1yearreturn', '3yearreturn', '5yearreturn',
  'Priceof52WeekHigh', 'RealizedVolatility30dAnnualized',
]);

const METRIC_HEADER_OVERRIDES: Record<string, string> = {
  RevGrowthhistCAGR3y: 'Rev CAGR (3y)',
  RevenueGrowthFwd1Y: 'Rev CAGR (1y)',
  Exp3YRevenueGrowthCAGR: 'Rev CAGR (3y)',
  RevenueFwd5YCAGR: 'Rev CAGR (5y)',
  EPSGrowthFY1: 'EPS Growth Y+1',
  EPSGrowthFY2: 'EPS Growth Y+2',
  EPSGrowthFY3: 'EPS Growth Y+3',
};

export function metricHeaderForField(
  field: string,
  columnMeta?: Record<string, { header?: string | null; short_name?: string | null }>,
): string {
  if (METRIC_HEADER_OVERRIDES[field]) return METRIC_HEADER_OVERRIDES[field];
  const meta = columnMeta?.[field];
  if (meta?.header) return String(meta.header);
  return field;
}

/**
 * Full, human parameter name for a metric — the plain-language line the old
 * Excel infographic used ("Historical Revenue Growth"), sitting above the
 * short metric code ("Rev CAGR (3y)"). Lets the sidebar drop group sub-headers
 * (Historical Revenue / Revenue Forecast / Earnings Forecast) and read as a flat
 * list of clearly-named parameters.
 */
const METRIC_FULL_NAME: Record<string, string> = {
  // Growth
  RevGrowthhistCAGR3y: 'Historical Growth',
  RevenueGrowthFwd1Y: 'Growth This Year',
  Exp3YRevenueGrowthCAGR: 'Medium-Term Growth',
  RevenueFwd5YCAGR: 'Long-Term Growth',
  EPSGrowthFY1: 'Earnings This Year',
  EPSGrowthFY2: 'Earnings Next Year',
  EPSGrowthFY3: 'Earnings in 2 Years',
  // Quality
  EbitMargin: 'EBIT Margin',
  NetIncomeMargin: 'Net Income Margin',
  FCFMargin: 'Free Cash Flow Margin',
  FCFMarginForecastFY1: 'FCF Margin This Year',
  FCFMarginForecastFY2: 'FCF Margin Next Year',
  FCFMarginForecastFY3: 'FCF Margin in 2 Years',
  NetDebtEBITDA: 'Net Debt / EBITDA',
  ROE: 'Return on Equity',
  ROA: 'Return on Assets',
  ROCE: 'Return on Capital Employed',
  EatningsQualityScore: 'Earnings Stability',
  // Value
  PEFWD: 'Forward P/E',
  PE3Y: 'P/E in 3 Years',
  PBV: 'Price / Book',
  EVEBITDAFWD: 'Forward EV / EBITDA',
  EVEBITFWD: 'Forward EV / EBIT',
  FCFYield: 'Free Cash Flow Yield',
  DivdendYield: 'Dividend Yield',
  NetIncomeYield: 'Earnings Yield',
  BuybackYield: 'Buyback Yield',
  Div3Yhistgrowth: 'Dividend Growth (3y)',
  FCFPayout: 'FCF Payout Ratio',
  NetIncomePayout: 'Earnings Payout Ratio',
  // Momentum
  '1monthreturn': '1-Month Return',
  '3monthreturn': '3-Month Return',
  YTDreturn: 'Year-to-Date Return',
  Priceof52WeekHigh: '% of 52-Week High',
  '1yearreturn': '1-Year Return',
  '3yearreturn': '3-Year Return',
  '5yearreturn': '5-Year Return',
  // Antifragility
  Beta: 'Beta',
  RealizedVolatility30dAnnualized: 'Realized Volatility (30d)',
};

/**
 * Descriptive parameter name (line 1). Falls back to the DB header, then the
 * short override code, so any un-mapped field still renders sensibly.
 */
export function metricFullNameForField(
  field: string,
  columnMeta?: Record<string, { header?: string | null; short_name?: string | null }>,
): string {
  if (METRIC_FULL_NAME[field]) return METRIC_FULL_NAME[field];
  const meta = columnMeta?.[field];
  if (meta?.header) return String(meta.header);
  return metricHeaderForField(field, columnMeta);
}

export const EPS_GROWTH_FIELDS = new Set([
  'EPSGrowthFY1', 'EPSGrowthFY2', 'EPSGrowthFY3',
]);

export function isEpsLossMetricValue(field: string, v: number): boolean {
  return EPS_GROWTH_FIELDS.has(field) && Math.round(v) <= -100;
}

export const METRIC_NOT_AVAILABLE = 'na';

/**
 * Valuation multiples that can never legitimately equal 0 — a 0/blank means the
 * metric is not computable for this company (very common for the Financials
 * sector: banks and insurers have no meaningful enterprise value or EV/EBITDA).
 * The DB still stores a percentile score for these (a blank/0 sorts as the
 * "cheapest" name → a high score), which is misleading, so we suppress both.
 */
export const MULTIPLE_FIELDS_NA_WHEN_ZERO = new Set([
  'PEFWD', 'PE3Y', 'PE', 'PBV', 'PS', 'PSFWD',
  'EVEBITDAFWD', 'EVEBITFWD', 'EVEBITDA', 'EVEBIT', 'EVSales', 'EVSalesFWD',
  'PEGFWD', 'PEG',
]);

/** Fields that are structurally not meaningful for the Financials sector. */
const FINANCIALS_NA_FIELDS = new Set([
  'EVEBITDAFWD', 'EVEBITFWD', 'EVEBITDA', 'EVEBIT', 'FCFYield', 'FCFMargin',
  'FCFMarginForecastFY1', 'FCFMarginForecastFY2', 'FCFMarginForecastFY3',
]);

export function isFinancialsSectorRow(stock?: Record<string, unknown> | null): boolean {
  const s = String(
    (stock as Record<string, unknown> | null | undefined)?.Sector
    ?? (stock as Record<string, unknown> | null | undefined)?.sector
    ?? '',
  ).toLowerCase();
  return s === 'financials' || s.includes('financial');
}

/**
 * A metric that should read as "N/A" (no value, no score) instead of a
 * misleading 0 paired with a spurious score. Two cases:
 *  1. A valuation multiple whose raw value is 0/blank (can't be 0 legitimately).
 *  2. An EV- or FCF-based metric on a Financials-sector company with no value.
 */
export function isMetricNotApplicable(
  field: string,
  raw: unknown,
  stock?: Record<string, unknown> | null,
): boolean {
  const v = typeof raw === 'number' ? raw : raw == null || raw === '' ? null : Number(raw);
  const isZeroish = v == null || !Number.isFinite(v) || v === 0;
  if (!isZeroish) return false;
  if (MULTIPLE_FIELDS_NA_WHEN_ZERO.has(field)) return true;
  if (stock && isFinancialsSectorRow(stock) && FINANCIALS_NA_FIELDS.has(field)) return true;
  return false;
}

export function isMetricUnavailable(
  raw: unknown,
  score?: number | null,
): boolean {
  if (raw == null || raw === '') return true;
  const v = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(v)) return true;
  if ((raw === 0 || raw === '0' || v === 0) && (score == null || score <= 1)) return true;
  return false;
}

export function metricDisplayValue(
  field: string,
  raw: unknown,
  columnMeta?: Record<string, { format?: { type?: string; decimals?: number; signed?: boolean } }>,
  score?: number | null,
): string {
  const v = typeof raw === 'number' ? raw : raw == null || raw === '' ? null : Number(raw);
  if (v != null && Number.isFinite(v) && isEpsLossMetricValue(field, v)) return 'Loss';
  if (isMetricUnavailable(raw, score)) return METRIC_NOT_AVAILABLE;
  return formatStockMetricValue(field, raw, columnMeta);
}

export function formatStockMetricValue(
  field: string,
  raw: unknown,
  columnMeta?: Record<string, { format?: { type?: string; decimals?: number; signed?: boolean } }>,
): string {
  const v = typeof raw === 'number' ? raw : raw == null || raw === '' ? null : Number(raw);
  if (v == null || !Number.isFinite(v)) return METRIC_NOT_AVAILABLE;
  if (isEpsLossMetricValue(field, v)) return 'Loss';
  const fmt = columnMeta?.[field]?.format;
  const isPct = fmt?.type === 'percent' || (!fmt?.type && FALLBACK_PERCENTAGE_FIELDS.has(field));
  const dec = typeof fmt?.decimals === 'number'
    ? fmt.decimals
    : (fmt?.type === 'percent' ? 0 : (fmt?.type === 'ratio' ? 2 : 0));
  const signed = !!fmt?.signed;
  if (isPct) {
    const sign = signed && v > 0 ? '+' : '';
    return `${sign}${v.toFixed(dec)}%`;
  }
  return dec > 0 ? v.toFixed(dec) : Math.round(v).toLocaleString();
}

export type MetricGroup = { label: string; fields: string[] };

/** Group layout mirrors Key Ratings strategy blocks in Ratings2Tab. */
export const STRATEGY_METRIC_GROUPS: Record<string, MetricGroup[]> = {
  PureGrowth: [
    { label: 'Historical Revenue', fields: ['RevGrowthhistCAGR3y'] },
    { label: 'Revenue Forecast', fields: ['RevenueGrowthFwd1Y', 'Exp3YRevenueGrowthCAGR', 'RevenueFwd5YCAGR'] },
    { label: 'Earnings Forecast', fields: ['EPSGrowthFY1', 'EPSGrowthFY2', 'EPSGrowthFY3'] },
  ],
  PureQuality: [
    // Col 1 = Margins + Debt, Col 2 = Efficiency (Strategy Metrics 2-col layout).
    { label: 'Margins', fields: ['EbitMargin', 'NetIncomeMargin', 'FCFMargin'] },
    { label: 'Debt', fields: ['NetDebtEBITDA'] },
    { label: 'Efficiency', fields: ['ROE', 'ROA', 'EatningsQualityScore'] },
  ],
  PureValue: [
    { label: 'Price Multiples', fields: ['PEFWD', 'PE3Y', 'PBV'] },
    { label: 'EV Multiples', fields: ['EVEBITDAFWD', 'EVEBITFWD'] },
    { label: 'Yield', fields: ['FCFYield', 'DivdendYield'] },
  ],
  PureMomentum: [
    { label: 'Short term', fields: ['1monthreturn', '3monthreturn', 'YTDreturn'] },
    { label: 'Mid term', fields: ['Priceof52WeekHigh', '1yearreturn'] },
    { label: 'Long term', fields: ['3yearreturn', '5yearreturn'] },
  ],
  PureAntifragility: [
    { label: 'Price Risk', fields: ['Beta', 'RealizedVolatility30dAnnualized'] },
    { label: 'Free Cash Flow', fields: ['FCFMargin', 'FCFYield'] },
    { label: 'Quality', fields: ['ROCE', 'EatningsQualityScore', 'NetDebtEBITDA'] },
  ],
};

/**
 * Per-metric weights inside each single-factor rating, mirrored from the DB
 * `strategy.strategyweights`, keyed by field. Order in the DB string does not
 * match the display grouping, so weights are matched by field, not position.
 * Displayed metrics that carry no weight (e.g. Momentum YTD) are omitted.
 *
 * Keep in sync with the `strategy` table. Source of truth is the DB; this is a
 * static mirror because the sidebars render from hand-authored field groups.
 */
export const STRATEGY_METRIC_WEIGHTS: Record<string, Record<string, number>> = {
  PureGrowth: {
    RevGrowthhistCAGR3y: 10,
    RevenueGrowthFwd1Y: 15,
    Exp3YRevenueGrowthCAGR: 35,
    RevenueFwd5YCAGR: 25,
    EPSGrowthFY1: 5,
    EPSGrowthFY2: 5,
    EPSGrowthFY3: 5,
  },
  PureQuality: {
    EbitMargin: 15,
    NetIncomeMargin: 15,
    FCFMargin: 15,
    ROE: 15,
    ROA: 15,
    EatningsQualityScore: 15,
    NetDebtEBITDA: 10,
  },
  PureValue: {
    PEFWD: 20,
    PE3Y: 20,
    EVEBITDAFWD: 15,
    EVEBITFWD: 15,
    FCFYield: 20,
    DivdendYield: 5,
    PBV: 5,
  },
  PureMomentum: {
    '1monthreturn': 15,
    '3monthreturn': 20,
    Priceof52WeekHigh: 20,
    '1yearreturn': 15,
    '3yearreturn': 15,
    '5yearreturn': 15,
  },
  PureAntifragility: {
    Beta: 30,
    RealizedVolatility30dAnnualized: 30,
    NetDebtEBITDA: 10,
    FCFMargin: 10,
    EatningsQualityScore: 10,
    FCFYield: 5,
    ROCE: 5,
  },
};

/** Weight (%) of a metric inside a single-factor rating, or null if none. */
export function getStrategyMetricWeight(
  strategyField: string,
  field: string,
): number | null {
  const w = STRATEGY_METRIC_WEIGHTS[strategyField]?.[field];
  return typeof w === 'number' && isFinite(w) ? w : null;
}

export function getMetricBarStyle(stock: Record<string, unknown>, field: string): { color: string; percent: number } {
  const sv = getMetricScoreValue(stock, field);
  if (sv == null) return { color: '#6b7280', percent: 0 };
  return { color: metricBarScoreColor(sv), percent: Math.max(0, Math.min(100, sv * 10)) };
}

export function shouldShowMetricDash(
  stock: Record<string, unknown>,
  field: string,
  raw: unknown,
): boolean {
  return isMetricUnavailable(raw, getMetricScoreValue(stock, field));
}

export type MetricChevronDir = 'up' | 'down' | 'neutral';

export type MetricChevronIndicator = {
  count: number;
  dir: MetricChevronDir;
  color: string;
  label: string;
};

/** 0–10 metric score → stacked chevrons + tier label (Insights sidebar pattern). */
export function metricScoreToChevrons(score: number | null | undefined): MetricChevronIndicator | null {
  if (score == null || !Number.isFinite(score) || score === 0) return null;

  const s = Math.round(score);
  let count: number;
  let dir: MetricChevronDir;
  let color: string;
  let label: string;

  if (s >= 9) {
    count = 2; dir = 'up'; color = '#4ade80'; label = 'VERY STRONG';
  } else if (s >= 7) {
    count = 1; dir = 'up'; color = '#4ade80'; label = 'STRONG';
  } else if (s >= 4) {
    count = 1; dir = 'neutral'; color = '#facc15'; label = 'AVERAGE';
  } else if (s >= 3) {
    count = 1; dir = 'down'; color = '#f87171'; label = 'WEAK';
  } else if (s >= 2) {
    count = 2; dir = 'down'; color = '#f87171'; label = 'WEAK';
  } else {
    count = 2; dir = 'down'; color = '#f87171'; label = 'VERY WEAK';
  }

  return { count, dir, color, label };
}

/** 0–100 strategy/factor rating → stacked chevrons (stock GEAR+ sidebar). Max 2 arrows. */
export function strategyRatingToChevrons(
  rating: number | null | undefined,
): MetricChevronIndicator | null {
  const tier = ratingTierId(rating);
  if (tier === 'none') return null;

  const label = scoreTierLabel(rating);
  if (tier === 'very_strong') {
    return { count: 2, dir: 'up', color: RATING_TIER_GREEN, label };
  }
  if (tier === 'strong') {
    return { count: 1, dir: 'up', color: RATING_TIER_GREEN, label };
  }
  if (tier === 'average') {
    return { count: 1, dir: 'neutral', color: RATING_TIER_YELLOW, label };
  }
  if (tier === 'weak') {
    return { count: 1, dir: 'down', color: RATING_TIER_RED, label };
  }
  return { count: 2, dir: 'down', color: RATING_TIER_RED, label };
}

export function getMetricChevronIndicator(
  stock: Record<string, unknown>,
  field: string,
): MetricChevronIndicator | null {
  return metricScoreToChevrons(getMetricScoreValue(stock, field));
}
